import { motion } from "framer-motion";
import { HelpCircle, CheckCircle2, Layers } from "lucide-react";
import SignatureInput from "./SignatureInput";

export default function Step4MotivationQuestions({ formData, setFormData, errors, motivationalQuestions = [], hideHeading = false }) {
  const handleChange = (sectionIndex, questionIndex, value) => {
    setFormData((prev) => {
      const prevAnswers = prev.motivationAnswers || [];
      const updatedAnswers = [...prevAnswers];

      if (!updatedAnswers[sectionIndex]) {
        updatedAnswers[sectionIndex] = [];
      }
      // Clone section row so we don't mutate previous state
      updatedAnswers[sectionIndex] = [...(updatedAnswers[sectionIndex] || [])];
      updatedAnswers[sectionIndex][questionIndex] = value;

      return {
        ...prev,
        motivationAnswers: updatedAnswers,
      };
    });
  };

  const handleMultipleChoiceToggle = (sectionIndex, questionIndex, option) => {
    setFormData((prev) => {
      const prevAnswers = prev.motivationAnswers || [];
      const updatedAnswers = [...prevAnswers];

      // Ensure we have a section slot and clone it so we don't mutate previous state
      if (!updatedAnswers[sectionIndex]) {
        updatedAnswers[sectionIndex] = [];
      }
      updatedAnswers[sectionIndex] = [...(updatedAnswers[sectionIndex] || [])];

      const currentAnswer = updatedAnswers[sectionIndex][questionIndex];
      const arr = Array.isArray(currentAnswer) ? [...currentAnswer] : [];
      const isSelected = arr.includes(option);

      updatedAnswers[sectionIndex][questionIndex] = isSelected
        ? arr.filter((item) => item !== option)
        : [...arr, option];

      return {
        ...prev,
        motivationAnswers: updatedAnswers,
      };
    });
  };

  // Check if motivationalQuestions is the new format (array of sections) or old format (array of questions)
  const isNewFormat =
    motivationalQuestions.length > 0 &&
    (motivationalQuestions[0]?.sectionTitle !== undefined ||
      motivationalQuestions[0]?.section_title !== undefined);

  if (!motivationalQuestions || motivationalQuestions.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
        className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm sm:p-8"
      >
        <div className="flex gap-3 items-center mb-8">
          <div className="flex justify-center items-center w-10 h-10 text-white bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg shadow-md">
            <HelpCircle size={20} />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Additional Information</h2>
        </div>
        <div className="py-12 text-center">
          <p className="text-gray-600">No additional questions for this event.</p>
        </div>
      </motion.div>
    );
  }

  const renderQuestion = (question, sectionIndex, questionIndex) => {
    const answer = formData.motivationAnswers?.[sectionIndex]?.[questionIndex] || "";
    const error = errors?.[`motivation_${sectionIndex}_${questionIndex}`];

    return (
      <motion.div
        key={`${sectionIndex}-${questionIndex}`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: questionIndex * 0.05 }}
        className="min-w-0 p-3 sm:p-4 bg-gradient-to-br from-orange-50 to-white rounded-lg border border-orange-100"
      >
        <label className="block mb-3 text-sm font-semibold text-gray-900 break-words">
          {questionIndex + 1}. {question.question}
          {question.required && <span className="ml-1 text-red-500">*</span>}
        </label>

        {/* Short Text Input */}
        {question.type === "short_text" && (
          <input
            type="text"
            value={answer}
            onChange={(e) => handleChange(sectionIndex, questionIndex, e.target.value)}
            placeholder={question.placeholder || "Enter your answer..."}
            className={`w-full px-4 py-3 text-gray-900 bg-white rounded-lg border transition-all outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
              error
                ? "bg-red-50 border-red-300"
                : "border-gray-300 hover:border-gray-400"
            }`}
          />
        )}

        {/* Long Text / Free Text */}
        {(question.type === "long_text" || question.type === "free_text") && (
          <textarea
            value={answer}
            onChange={(e) => handleChange(sectionIndex, questionIndex, e.target.value)}
            rows={4}
            placeholder={question.placeholder || "Enter your answer..."}
            className={`w-full px-4 py-3 text-gray-900 bg-white rounded-lg border transition-all outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none ${
              error
                ? "bg-red-50 border-red-300"
                : "border-gray-300 hover:border-gray-400"
            }`}
          />
        )}

        {/* Single Choice / Radio */}
        {question.type === "single_choice" && (
          <div className="space-y-2 min-w-0">
            {question.options?.map((option, optionIndex) => {
              const isSelected = answer === option;
              return (
                <motion.button
                  key={optionIndex}
                  type="button"
                  onClick={() => handleChange(sectionIndex, questionIndex, option)}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  className={`w-full min-w-0 flex gap-3 items-start p-3 text-left rounded-lg border-2 transition-all touch-manipulation ${
                    isSelected
                      ? "text-orange-900 bg-orange-50 border-orange-500"
                      : "text-gray-700 bg-white border-gray-200 hover:border-orange-300"
                  }`}
                >
                  <div
                    className={`mt-0.5 flex shrink-0 justify-center items-center w-5 h-5 rounded-full border-2 transition-all ${
                      isSelected
                        ? "bg-orange-500 border-orange-500"
                        : "border-gray-300"
                    }`}
                  >
                    {isSelected && (
                      <div className="w-2.5 h-2.5 bg-white rounded-full" />
                    )}
                  </div>
                  <span className="flex-1 min-w-0 text-sm font-medium text-left break-words leading-snug">
                    {option}
                  </span>
                </motion.button>
              );
            })}
          </div>
        )}

        {/* Multiple Choice / Checkboxes */}
        {question.type === "multiple_choice" && (
          <div className="space-y-2 min-w-0">
            {question.options?.map((option, optionIndex) => {
              const isSelected = Array.isArray(answer) && answer.includes(option);
              return (
                <motion.button
                  key={optionIndex}
                  type="button"
                  onClick={() => handleMultipleChoiceToggle(sectionIndex, questionIndex, option)}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  className={`w-full min-w-0 flex gap-3 items-start p-3 text-left rounded-lg border-2 transition-all touch-manipulation ${
                    isSelected
                      ? "text-orange-900 bg-orange-50 border-orange-500"
                      : "text-gray-700 bg-white border-gray-200 hover:border-orange-300"
                  }`}
                >
                  <div
                    className={`mt-0.5 flex shrink-0 justify-center items-center w-5 h-5 rounded border-2 transition-all ${
                      isSelected
                        ? "bg-orange-500 border-orange-500"
                        : "border-gray-300"
                    }`}
                  >
                    {isSelected && <CheckCircle2 size={14} className="text-white shrink-0" />}
                  </div>
                  <span className="flex-1 min-w-0 text-sm font-medium text-left break-words leading-snug">
                    {option}
                  </span>
                </motion.button>
              );
            })}
          </div>
        )}

        {/* Dropdown */}
        {question.type === "dropdown" && (
          <select
            value={answer}
            onChange={(e) => handleChange(sectionIndex, questionIndex, e.target.value)}
            className={`w-full px-4 py-3 text-gray-900 bg-white rounded-lg border transition-all outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
              error
                ? "bg-red-50 border-red-300"
                : "border-gray-300 hover:border-gray-400"
            }`}
          >
            <option value="">-- Select an option --</option>
            {question.options?.map((option, optionIndex) => (
              <option key={optionIndex} value={option}>
                {option}
              </option>
            ))}
          </select>
        )}

        {/* Date Input */}
        {question.type === "date" && (
          <input
            type="date"
            value={answer}
            onChange={(e) => handleChange(sectionIndex, questionIndex, e.target.value)}
            className={`w-full px-4 py-3 text-gray-900 bg-white rounded-lg border transition-all outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
              error
                ? "bg-red-50 border-red-300"
                : "border-gray-300 hover:border-gray-400"
            }`}
          />
        )}

        {/* Signature */}
        {question.type === "signature" && (
          <SignatureInput
            value={answer}
            onChange={(value) => handleChange(sectionIndex, questionIndex, value)}
            error={error}
          />
        )}

        {error && (
          <p className="mt-2 text-sm text-red-500">{error}</p>
        )}
      </motion.div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="min-w-0 p-4 bg-white rounded-xl border border-gray-200 shadow-sm sm:p-8"
    >
      {!hideHeading && (
        <div className="flex gap-3 items-center mb-6 sm:mb-8 min-w-0">
          <div className="flex shrink-0 justify-center items-center w-10 h-10 text-white bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg shadow-md">
            <Layers size={20} />
          </div>
          <h2 className="min-w-0 text-xl font-bold text-gray-900 break-words sm:text-2xl">
            Additional Information
          </h2>
        </div>
      )}

      <div className="space-y-8">
        {isNewFormat ? (
          // New format with sections
          motivationalQuestions.map((section, sectionIndex) => (
            <div key={sectionIndex} className="space-y-4">
              {/* Section Header */}
              <div className="pb-3 border-b-2 border-orange-200 min-w-0">
                <h3 className="text-base font-bold text-gray-900 uppercase break-words sm:text-lg">
                  {section.sectionTitle ?? section.section_title ?? `Section ${sectionIndex + 1}`}
                </h3>
              </div>

              {/* Section Questions */}
              <div className="space-y-4">
                {section.questions?.map((question, questionIndex) =>
                  renderQuestion(question, sectionIndex, questionIndex)
                )}
              </div>
            </div>
          ))
        ) : (
          // Old format - backward compatibility
          <div className="space-y-4">
            {motivationalQuestions.map((question, index) => {
              const answer = formData.motivationAnswers?.[index] || "";
              const error = errors?.[`motivation_${index}`];

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="min-w-0 p-3 sm:p-4 bg-gradient-to-br from-orange-50 to-white rounded-lg border border-orange-100"
                >
                  <label className="block mb-3 text-sm font-semibold text-gray-900 break-words">
                    {question.question} <span className="text-red-500">*</span>
                  </label>

                  {question.type === "free_text" ? (
                    <textarea
                      value={answer}
                      onChange={(e) => handleChange(0, index, e.target.value)}
                      rows={4}
                      className={`w-full px-4 py-3 text-gray-900 bg-white rounded-lg border transition-all outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none ${
                        error
                          ? "bg-red-50 border-red-300"
                          : "border-gray-300 hover:border-gray-400"
                      }`}
                      placeholder="Enter your answer..."
                    />
                  ) : question.type === "multiple_choice" ? (
                    <div className="space-y-2 min-w-0">
                      {question.options?.map((option, optionIndex) => {
                        const isSelected = answer === option;
                        return (
                          <motion.button
                            key={optionIndex}
                            type="button"
                            onClick={() => handleChange(0, index, option)}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className={`w-full min-w-0 flex gap-3 items-start p-3 text-left rounded-lg border-2 transition-all touch-manipulation ${
                              isSelected
                                ? "text-orange-900 bg-orange-50 border-orange-500"
                                : "text-gray-700 bg-white border-gray-200 hover:border-orange-300"
                            }`}
                          >
                            <div
                              className={`mt-0.5 flex shrink-0 justify-center items-center w-5 h-5 rounded-full border-2 transition-all ${
                                isSelected
                                  ? "bg-orange-500 border-orange-500"
                                  : "border-gray-300"
                              }`}
                            >
                              {isSelected && (
                                <CheckCircle2 size={14} className="text-white shrink-0" />
                              )}
                            </div>
                            <span className="flex-1 min-w-0 text-sm font-medium text-left break-words leading-snug">
                              {option}
                            </span>
                          </motion.button>
                        );
                      })}
                    </div>
                  ) : null}

                  {error && (
                    <p className="mt-2 text-sm text-red-500">{error}</p>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </motion.div>
  );
}

